import React from 'react';
import {
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import moment from 'moment';
import { Link, useLocation } from 'react-router-dom';

import {
  DashboardError,
  DashboardPage,
  JobPublicationDetails,
} from '../../components/JobsAccount';
import { CPGJOBS_SITE_URL } from '../../components/CpgChrome/cpgSites';
import { book } from '../../routers/books';
import { useGetJobsAccountHomeQuery } from '../../services/serviceJobsAccount';
import {
  getJobsAccountRole,
  getJobsAccountRolePath,
  hasBothJobsAccountRoles,
} from '../../utils/jobsAccountRole';
import {
  IJobsAccountHome,
  IJobsAccountHomePackage,
  IJobsAccountHomeListing,
  IMyJobApplication,
} from '../../services/serviceJobsAccount/interfaces';
import {
  IResumeContactRequest,
  ResumeDatabaseAccessState,
  useGetResumeDatabaseAccessQuery,
  useGetSeekerResumeContactRequestsQuery,
} from '../../services/serviceResume';
import {
  getPostPrepaidJobPath,
  getPrepaidPurchasePath,
} from '../../utils/jobAccountRoutes';

const monoLabelStyles = {
  color: 'var(--accent-text)',
  fontSize: '0.75rem',
  fontWeight: 700,
} as const;

const actionButtonStyles = {
  borderRadius: 0,
  boxShadow: '4px 4px 0 var(--accent-soft)',
  fontSize: '0.85rem',
  px: 3,
  py: 1.4,
  textTransform: 'none',
  '&:hover': {
    boxShadow: '4px 4px 0 var(--accent-soft)',
  },
} as const;

const formatDate = (date: string | null): string => (
  date ? moment(date).format('MMM D, YYYY') : '—'
);

const SectionHeading = ({
  title,
  linkLabel,
  to,
}: {
  title: string;
  linkLabel: string;
  to: string;
}) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    sx={{
      borderTop: '2px solid var(--line)',
      pb: 1.5,
      pt: 2,
    }}
  >
    <Typography
      component="h2"
      sx={{
        color: 'var(--ink)',
        fontFamily: 'var(--font-display)',
        fontSize: { xs: '1.35rem', md: '1.55rem' },
        fontWeight: 800,
      }}
    >
      {title}
    </Typography>
    <Box
      component={Link}
      to={to}
      sx={{
        ...monoLabelStyles,
        textDecoration: 'none',
        '&:hover': { color: 'var(--accent-deep)' },
      }}
    >
      {linkLabel} →
    </Box>
  </Stack>
);

const ApplicationRow = ({ application }: { application: IMyJobApplication }) => (
  <Stack
    direction={{ xs: 'column', sm: 'row' }}
    alignItems={{ xs: 'flex-start', sm: 'center' }}
    justifyContent="space-between"
    spacing={2}
    sx={{
      borderBottom: '1px solid var(--line-soft)',
      py: 2.25,
    }}
  >
    <Box>
      <JobPublicationDetails
        title={application.jobTitle || 'Job listing'}
        path={application.slug}
        siteId={application.siteId}
        isActive={application.isActive}
        titleSx={{
          color: 'var(--ink)',
          fontSize: '1rem',
          fontWeight: 800,
          textDecoration: 'none',
          '&:hover': { color: 'var(--accent-text)' },
        }}
      />
      <Typography
        sx={{
          color: 'var(--ink-3)',
          fontSize: '0.8rem',
          mt: 0.6,
        }}
      >
        {application.company || 'Company not listed'} · Applied {formatDate(application.dateApplied)}
      </Typography>
    </Box>
    <Chip
      label={application.viewed ? 'Viewed' : 'Submitted'}
      size="small"
      sx={{
        backgroundColor: application.viewed ? 'var(--go-soft)' : 'var(--bg-2)',
        borderRadius: 0,
        color: application.viewed ? 'var(--go-deep)' : 'var(--ink-3)',
        fontSize: '0.8rem',
        fontWeight: 700,
      }}
    />
  </Stack>
);

const EmptyRows = ({ children }: { children: React.ReactNode }) => (
  <Typography
    color="text.secondary"
    sx={{
      borderBottom: '1px solid var(--line-soft)',
      py: 3,
    }}
  >
    {children}
  </Typography>
);

const SeekerHome = ({
  applications,
  applicationsPath,
  hasEmployerActivity,
  savedJobCount,
  hasResume,
  resumeVisibility,
  resumeCompleteness,
  contactRequests,
}: {
  applications: IMyJobApplication[];
  applicationsPath: string;
  hasEmployerActivity: boolean;
  savedJobCount: number;
  hasResume: boolean;
  resumeVisibility: IJobsAccountHome['resumeVisibility'];
  resumeCompleteness: number;
  contactRequests: IResumeContactRequest[];
}) => (
  <>
    <Paper
      variant="outlined"
      sx={{
        backgroundColor: resumeVisibility !== 'Searchable' && resumeVisibility !== 'Public'
          ? 'var(--accent-soft)'
          : 'var(--go-soft)',
        borderColor: resumeVisibility !== 'Searchable' && resumeVisibility !== 'Public'
          ? 'var(--accent)'
          : 'var(--go)',
        borderRadius: 0,
        mb: 5,
        p: { xs: 2.5, md: 3 },
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
        spacing={2.5}
      >
        <Box>
          <Typography sx={monoLabelStyles}>CPG Talent Network</Typography>
          <Typography component="h2" variant="h6" sx={{ mt: 0.6 }}>
            {!hasResume
              ? 'Let CPG employers discover your experience'
              : resumeVisibility === 'Private'
                ? 'Your resume is private'
                : resumeVisibility === 'Searchable'
                  ? 'You are searchable, with contact details hidden'
                  : resumeVisibility === 'Public'
                    ? 'Your public resume is visible to eligible employers'
                    : 'Choose how employers can find you'}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.6, maxWidth: 760 }}>
            {!hasResume
              ? 'Build a CPG-focused resume, then choose Searchable or Public when you are ready to join the Talent Network.'
              : resumeVisibility === 'Private'
                ? 'Finish your profile, then choose Searchable or Public to join the Talent Network. You control what employers can see.'
                : resumeVisibility === 'Searchable'
                  ? 'Employers with an active Featured listing can find your masked profile and ask you to share contact details.'
                  : resumeVisibility === 'Public'
                    ? 'Employers with an active Featured listing can view the contact details and resume file you saved.'
                    : 'Review your visibility choices before joining the Talent Network.'}
          </Typography>
        </Box>
        <Button component={Link} to={book.myResume} variant="contained" sx={{ flexShrink: 0 }}>
          {!hasResume
            ? 'Create my resume'
            : resumeVisibility === 'Private' || !resumeVisibility
              ? 'Choose visibility'
              : 'Review my resume'}
        </Button>
      </Stack>
    </Paper>
    <Grid container sx={{ border: '2px solid var(--line)', boxShadow: '6px 6px 0 var(--accent-soft)', mb: 5 }}>
      <EmployerStat
        label="Saved jobs"
        value={savedJobCount}
        detail="Your shortlist"
        to={book.savedJobs}
        actionLabel="View saved jobs"
      />
      <EmployerStat
        label="Resume completeness"
        value={resumeCompleteness}
        detail={hasResume ? 'Keep your profile current' : 'Build your resume'}
        to={book.myResume}
        actionLabel={hasResume ? 'Edit your resume' : 'Build your resume'}
      />
    </Grid>
    {contactRequests.length ? (
      <Box sx={{ backgroundColor: 'var(--accent-soft)', border: '1px solid var(--accent)', mb: 5, p: 2.5 }}>
        <Typography sx={monoLabelStyles}>Employers want to connect</Typography>
        {contactRequests.map((request) => (
          <Typography key={`${request.rguid}-${request.createdDate}`} sx={{ mt: 1 }}>
            <strong>{request.employerName}</strong>
            {request.listingTitle ? ` · ${request.listingTitle}` : ''}. Use the link in the email they sent to share your details, decline, block, or report it.
          </Typography>
        ))}
        <Typography color="text.secondary" sx={{ mt: 1.5 }}>
          Every employer who has contacted you is listed under{' '}
          <Box component={Link} to={book.myResume} sx={{ color: 'var(--accent-text)', fontWeight: 800 }}>
            employer contact history
          </Box>
          {' '}on My Resume.
        </Typography>
      </Box>
    ) : null}
    <SectionHeading
      title="Your applications"
      linkLabel="View all"
      to={applicationsPath}
    />
    {applications.length ? (
      applications.map((application) => (
        <ApplicationRow
          key={application.applicationId}
          application={application}
        />
      ))
    ) : (
      <EmptyRows>
        You have not submitted any applications through CPG Jobs yet.
      </EmptyRows>
    )}
    <Typography
      sx={{
        color: 'var(--ink-3)',
        fontSize: '0.78rem',
        mt: 1.5,
      }}
    >
      “Viewed” means the employer opened your application.
    </Typography>
    <Box
      sx={{
        backgroundColor: 'var(--bg-2)',
        mt: { xs: 5, md: 7 },
        p: { xs: 2.5, md: 3 },
      }}
    >
      <Typography color="text.secondary">
        {hasEmployerActivity ? 'You also manage job listings. ' : 'Hiring for your company? '}
        {hasEmployerActivity ? (
          <Box
            component={Link}
            to={getJobsAccountRolePath('employer')}
            sx={{ color: 'var(--accent-text)', fontWeight: 800 }}
          >
            Switch to your employer view.
          </Box>
        ) : (
          <Box
            component={Link}
            to={book.postAJob}
            sx={{ color: 'var(--accent-text)', fontWeight: 800 }}
          >
            Post a job and reach CPG candidates.
          </Box>
        )}
      </Typography>
    </Box>
  </>
);

const EmployerStat = ({
  label,
  value,
  detail,
  to,
  actionLabel,
}: {
  label: string;
  value: number;
  detail?: string;
  to: string;
  actionLabel: string;
}) => (
  <Grid
    item
    xs={6}
    md={3}
    sx={{
      borderBottom: { xs: '1px solid var(--line-soft)', md: 0 },
      borderRight: {
        xs: '1px solid var(--line-soft)',
        md: '1px solid var(--line-soft)',
      },
      '&:nth-of-type(2n)': {
        borderRight: { xs: 0, md: '1px solid var(--line-soft)' },
      },
      '&:last-of-type': {
        borderRight: 0,
      },
    }}
  >
    <Box
      component={Link}
      to={to}
      aria-label={`${label}: ${value}. ${actionLabel}`}
      sx={{
        color: 'inherit',
        display: 'block',
        height: '100%',
        p: { xs: 2, md: 2.5 },
        textDecoration: 'none',
        transition: 'background-color 120ms ease',
        '&:hover': {
          backgroundColor: 'var(--bg-2)',
        },
        '&:focus-visible': {
          outline: '3px solid var(--accent)',
          outlineOffset: -3,
        },
      }}
    >
      <Typography sx={monoLabelStyles}>{label}</Typography>
      <Typography
        sx={{
          color: 'var(--ink)',
          fontFamily: 'var(--font-display)',
          fontSize: { xs: '2rem', md: '2.5rem' },
          fontWeight: 900,
          lineHeight: 1.1,
          mt: 0.9,
        }}
      >
        {value}
      </Typography>
      {detail ? (
        <Typography color="text.secondary" sx={{ fontSize: '0.78rem', mt: 0.6 }}>
          {detail}
        </Typography>
      ) : null}
      <Typography sx={{ ...monoLabelStyles, fontSize: '0.65rem', mt: 1.2 }}>
        {actionLabel} →
      </Typography>
    </Box>
  </Grid>
);

const ListingRow = ({
  listing,
}: {
  listing: IJobsAccountHomeListing;
}) => (
  <Stack
    direction={{ xs: 'column', sm: 'row' }}
    justifyContent="space-between"
    spacing={2}
    sx={{
      borderBottom: '1px solid var(--line-soft)',
      py: 2.25,
    }}
  >
    <Box>
      <JobPublicationDetails
        title={listing.title || 'Job listing'}
        path={listing.path}
        siteId={listing.siteId}
        isActive={listing.isActive}
        titleSx={{
          color: 'var(--ink)',
          fontWeight: 800,
          textDecoration: 'none',
          '&:hover': { color: 'var(--accent-text)' },
        }}
      />
      <Typography
        sx={{
          color: 'var(--ink-3)',
          fontSize: '0.8rem',
          mt: 0.6,
        }}
      >
        {listing.location || 'Location not listed'} · {listing.isActive ? 'Expires' : 'Inactive'}{' '}
        {formatDate(listing.endDate)}
      </Typography>
    </Box>
    <Box sx={{ minWidth: 125, textAlign: { xs: 'left', sm: 'right' } }}>
      {listing.eguid ? (
        <Box
          component={Link}
          to={book.jobsApplicants(listing.eguid)}
          sx={{
            color: 'var(--ink)',
            fontWeight: 800,
            textDecoration: 'none',
            '&:hover': { color: 'var(--accent-text)' },
          }}
        >
          {listing.applicantCount} applicant{listing.applicantCount === 1 ? '' : 's'}
        </Box>
      ) : (
        <Typography fontWeight={800}>
          {listing.applicantCount} applicant{listing.applicantCount === 1 ? '' : 's'}
        </Typography>
      )}
      {listing.newApplicantCount > 0 ? (
        <Typography sx={{ ...monoLabelStyles, fontSize: '0.67rem', mt: 0.4 }}>
          +{listing.newApplicantCount} this week
        </Typography>
      ) : null}
    </Box>
  </Stack>
);

const PrepaidPackageRow = ({
  pkg,
}: {
  pkg: IJobsAccountHomePackage;
}) => (
  <Stack
    direction={{ xs: 'column', sm: 'row' }}
    alignItems={{ xs: 'flex-start', sm: 'center' }}
    justifyContent="space-between"
    spacing={2}
    sx={{
      borderTop: '1px solid var(--line-soft)',
      py: 2,
    }}
  >
    <Box>
      <Typography sx={monoLabelStyles}>
        {pkg.isFeatured ? 'Featured' : 'Standard'} package
        {pkg.type ? ` · ${pkg.type}` : ''}
      </Typography>
      <Typography fontWeight={800} sx={{ mt: 0.4 }}>
        {pkg.remainingCount} of {pkg.totalCount} listing credits remaining
      </Typography>
      <Typography color="text.secondary" sx={{ fontSize: '0.8rem', mt: 0.3 }}>
        Expires {formatDate(pkg.expirationDate)}
      </Typography>
    </Box>
    {pkg.prepaidGuid ? (
      <Button
        component={Link}
        to={getPostPrepaidJobPath(pkg.siteId, pkg.prepaidGuid)}
        variant="contained"
        sx={{
          ...actionButtonStyles,
          fontSize: '0.7rem',
          px: 2,
          whiteSpace: 'nowrap',
        }}
      >
        Use Listing Credit
      </Button>
    ) : null}
  </Stack>
);

// The Resume Database callout branches on the explicit access state so a paused,
// terms-pending, or historical-approval employer is not shown the Featured upsell.
const resumeCallout = (
  state: ResumeDatabaseAccessState
): { heading: string; body: string; actionLabel: string } => {
  switch (state) {
    case 'granted':
      return {
        heading: 'Find candidates beyond your applicant list',
        body: 'Search CPG resumes now. Candidates stay anonymous, and their contact details stay '
          + 'hidden, until they approve an introduction for one of your Featured listings.',
        actionLabel: 'Search resumes',
      };
    case 'termsRequired':
      return {
        heading: 'Accept the Resume Database terms to start searching',
        body: 'Your Featured listing unlocks the Talent Network. Read and accept the current usage '
          + 'terms and your search opens right away.',
        actionLabel: 'Review the terms',
      };
    case 'suspended':
      return {
        heading: 'Resume Database access is paused',
        body: 'A BevNET administrator paused Resume Database access for this account. Your job '
          + 'listings and applicants are unaffected.',
        actionLabel: 'See what this means',
      };
    case 'historicalOnly':
      return {
        heading: 'Your approved candidates are still available',
        body: 'Candidates who already approved you stay reachable. A new active Featured listing '
          + 'restores search and new introductions.',
        actionLabel: 'View approved candidates',
      };
    default:
      return {
        heading: 'Reach CPG candidates with a Featured listing',
        body: 'An active Featured job listing unlocks resume search. Candidates stay anonymous until '
          + 'they approve your introduction request.',
        actionLabel: 'Search resumes',
      };
  }
};

const EmployerHome = ({
  activeListingCount,
  totalApplicantCount,
  newApplicantCount,
  listings,
  currentPackages,
  listingsPath,
  hasSeekerActivity,
  canManageListings,
  resumeDatabaseState,
}: {
  activeListingCount: number;
  totalApplicantCount: number;
  newApplicantCount: number;
  listings: IJobsAccountHomeListing[];
  currentPackages: IJobsAccountHomePackage[];
  listingsPath: string;
  hasSeekerActivity: boolean;
  canManageListings: boolean;
  resumeDatabaseState: ResumeDatabaseAccessState | undefined;
}) => {
  const hasPackages = currentPackages.length > 0;
  const singlePackage = currentPackages.length === 1 ? currentPackages[0] : null;
  const totalRemaining = currentPackages.reduce(
    (sum, pkg) => sum + pkg.remainingCount,
    0
  );
  const packageSiteId = currentPackages[0]?.siteId ?? null;

  const remainingTo = (() => {
    if (singlePackage?.prepaidGuid) {
      return book.jobsPrepaidDetail(singlePackage.prepaidGuid);
    }
    if (hasPackages) {
      return book.jobsPrepaid;
    }
    return getPrepaidPurchasePath(packageSiteId);
  })();

  const resumeState = resumeDatabaseState || 'notEligible';
  const resumeIsOpen = resumeState === 'granted'
    || resumeState === 'termsRequired'
    || resumeState === 'historicalOnly';
  const callout = resumeCallout(resumeState);

  return (
    <>
      {resumeState !== 'notEligible' || canManageListings ? (
        <Paper
          variant="outlined"
          sx={{
            backgroundColor: resumeIsOpen ? 'var(--go-soft)' : 'var(--accent-soft)',
            borderColor: resumeIsOpen ? 'var(--go)' : 'var(--accent)',
            borderRadius: 0,
            mb: 5,
            p: { xs: 2.5, md: 3 },
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            spacing={2.5}
          >
            <Box>
              <Typography sx={monoLabelStyles}>Resume Database</Typography>
              <Typography component="h2" variant="h6" sx={{ mt: 0.6 }}>
                {callout.heading}
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 0.6, maxWidth: 760 }}>
                {callout.body}
              </Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ flexShrink: 0 }}>
              {resumeState === 'notEligible' ? (
                <>
                  <Button component={Link} to={book.postAJob} variant="contained">Post a Featured job</Button>
                  <Button component={Link} to={book.jobsListings} variant="outlined">Manage listings</Button>
                </>
              ) : (
                <Button component={Link} to={book.resumeDatabase} variant="contained">
                  {callout.actionLabel}
                </Button>
              )}
            </Stack>
          </Stack>
        </Paper>
      ) : null}
      <Grid
        container
        sx={{
          border: '2px solid var(--line)',
          boxShadow: '6px 6px 0 var(--accent-soft)',
          mb: { xs: 5, md: 7 },
        }}
      >
        {canManageListings ? (
          <EmployerStat
            label="Active listings"
            value={activeListingCount}
            detail="Currently live"
            to={listingsPath}
            actionLabel="Manage listings"
          />
        ) : null}
        <EmployerStat
          label="Total applicants"
          value={totalApplicantCount}
          detail="Across all listings"
          to={book.jobsAllApplicants}
          actionLabel="Review applicants"
        />
        <EmployerStat
          label="New this week"
          value={newApplicantCount}
          detail="Last 7 days"
          to={book.jobsNewApplicants}
          actionLabel="Review applicants"
        />
        {canManageListings ? (
          <EmployerStat
            label="Prepaid remaining"
            value={totalRemaining}
            detail={(() => {
              if (!hasPackages) return 'No active package';
              if (singlePackage) return `Expires ${formatDate(singlePackage.expirationDate)}`;
              return `Across ${currentPackages.length} packages`;
            })()}
            to={remainingTo}
            actionLabel={hasPackages ? 'Manage packages' : 'Buy a package'}
          />
        ) : null}
      </Grid>

      {canManageListings ? (
      <Box
        sx={{
          backgroundColor: 'var(--bg-2)',
          mb: { xs: 5, md: 7 },
          p: { xs: 2.5, md: 3 },
        }}
      >
        {hasPackages ? (
          <>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
              spacing={2}
            >
              <Box>
                <Typography sx={monoLabelStyles}>
                  Prepaid packages
                </Typography>
                <Typography fontWeight={800} sx={{ mt: 0.6 }}>
                  {totalRemaining} listing credit{totalRemaining === 1 ? '' : 's'} remaining
                  {currentPackages.length > 1
                    ? ` across ${currentPackages.length} packages`
                    : ''}
                </Typography>
                <Typography color="text.secondary" sx={{ fontSize: '0.8rem', mt: 0.3 }}>
                  Use a credit to post a job from any package below.
                </Typography>
              </Box>
              <Button
                component={Link}
                to={getPrepaidPurchasePath(packageSiteId)}
                variant="outlined"
                sx={{
                  ...actionButtonStyles,
                  boxShadow: 'none',
                  '&:hover': { boxShadow: 'none' },
                  fontSize: '0.7rem',
                  px: 2,
                  whiteSpace: 'nowrap',
                }}
              >
                Buy Another Package
              </Button>
            </Stack>
            <Box sx={{ mt: 1 }}>
              {currentPackages.map((pkg) => (
                <PrepaidPackageRow
                  key={pkg.prepaidGuid}
                  pkg={pkg}
                />
              ))}
            </Box>
          </>
        ) : (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            spacing={2}
          >
            <Box>
              <Typography sx={monoLabelStyles}>
                Job packages
              </Typography>
              <Typography fontWeight={800} sx={{ mt: 0.6 }}>
                Save when you are hiring for multiple roles
              </Typography>
              <Typography color="text.secondary" sx={{ fontSize: '0.8rem', mt: 0.3 }}>
                Buy a multi-job package and use the credits whenever you are ready.
              </Typography>
            </Box>
            <Button
              component={Link}
              to={getPrepaidPurchasePath(packageSiteId)}
              variant="contained"
              sx={{
                ...actionButtonStyles,
                fontSize: '0.7rem',
                px: 2,
                whiteSpace: 'nowrap',
              }}
            >
              Buy a Job Package
            </Button>
          </Stack>
        )}
      </Box>
      ) : null}

      <SectionHeading
        title={canManageListings ? 'Your listings' : 'Jobs you review'}
        linkLabel={canManageListings ? 'Manage all' : 'View applicants'}
        to={canManageListings ? listingsPath : book.jobsAllApplicants}
      />
      {listings.length ? (
        listings.map((listing) => (
          <ListingRow
            key={listing.id}
            listing={listing}
          />
        ))
      ) : (
        <EmptyRows>{canManageListings ? 'You do not have any job listings yet.' : 'No candidate-access jobs are available.'}</EmptyRows>
      )}

      <Typography
        color="text.secondary"
        sx={{
          borderTop: '1px solid var(--line-soft)',
          mt: { xs: 5, md: 7 },
          pt: 2.5,
        }}
      >
        {hasSeekerActivity ? 'You also have job applications. ' : 'Looking for your next role? '}
        {hasSeekerActivity ? (
          <Box
            component={Link}
            to={getJobsAccountRolePath('jobSeeker')}
            sx={{ color: 'var(--accent-text)', fontWeight: 800 }}
          >
            Switch to your job seeker view.
          </Box>
        ) : (
          <Box
            component="a"
            href={`${CPGJOBS_SITE_URL}/jobs`}
            sx={{ color: 'var(--accent-text)', fontWeight: 800 }}
          >
            Browse CPG jobs.
          </Box>
        )}
      </Typography>
    </>
  );
};

export const JobsAccountHome = () => {
  const { pathname } = useLocation();
  const { data, error, isLoading, isFetching } = useGetJobsAccountHomeQuery();
  const role = getJobsAccountRole(data, pathname);
  const hasBothRoles = hasBothJobsAccountRoles(data);
  const isEmployer = role === 'employer';
  const contactRequests = useGetSeekerResumeContactRequestsQuery(undefined, {
    skip: !data || isEmployer,
  });
  const resumeAccess = useGetResumeDatabaseAccessQuery(undefined, {
    skip: !data || !isEmployer,
  });
  const alternateRole = isEmployer ? 'jobSeeker' : 'employer';
  const name = data?.firstName?.trim();
  const company = data?.company?.trim();

  return (
    <DashboardPage
      title="CPG Jobs Account"
      hideTitle
      isLoading={isLoading || isFetching}
    >
      <DashboardError
        error={error}
        fallback="We could not load your CPG Jobs account. Please try again."
      />
      {data ? (
        <Box sx={{ pt: { xs: 4.5, md: 7 } }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'flex-end' }}
            justifyContent="space-between"
            spacing={3}
            sx={{ mb: { xs: 5, md: 7 } }}
          >
            <Box>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={{ xs: 0.8, sm: 2 }}
              >
                <Typography sx={monoLabelStyles}>
                  My account · {isEmployer ? 'Employer' : 'Job seeker'}
                </Typography>
                {hasBothRoles ? (
                  <Box
                    component={Link}
                    to={getJobsAccountRolePath(alternateRole)}
                    sx={{
                      ...monoLabelStyles,
                      color: 'var(--ink-3)',
                      fontSize: '0.68rem',
                      textDecoration: 'underline',
                      textUnderlineOffset: 3,
                      '&:hover': { color: 'var(--accent-text)' },
                    }}
                  >
                    Switch to {isEmployer ? 'job seeker' : 'employer'} view
                  </Box>
                ) : null}
              </Stack>
              <Typography
                component="h1"
                sx={{
                  color: 'var(--ink)',
                  fontFamily: 'var(--font-display)',
                  fontSize: { xs: '2.25rem', sm: '3rem', md: '3.6rem' },
                  fontWeight: 900,
                  letterSpacing: '-0.045em',
                  lineHeight: 1.05,
                  mt: 1.1,
                }}
              >
                Welcome back{name ? `, ${name}` : ''}
              </Typography>
              <Typography
                color="text.secondary"
                sx={{
                  fontSize: { xs: '1rem', md: '1.08rem' },
                  mt: 1.4,
                }}
              >
                {isEmployer
                  ? `Hiring${company ? ` for ${company}` : ''}: your listings, applicants, and packages.`
                  : 'Track your applications and keep your search moving.'}
              </Typography>
            </Box>
            <Button
              component={isEmployer ? Link : 'a'}
              to={isEmployer ? (data.hasListings ? book.postAJob : book.jobsAllApplicants) : undefined}
              href={isEmployer ? undefined : `${CPGJOBS_SITE_URL}/jobs`}
              variant="contained"
              sx={actionButtonStyles}
            >
              {isEmployer ? (data.hasListings ? 'Post a Job' : 'View Applicants') : 'Browse Jobs'}
            </Button>
          </Stack>

          {isEmployer ? (
            <EmployerHome
              activeListingCount={data.activeListingCount}
              totalApplicantCount={data.totalApplicantCount}
              newApplicantCount={data.newApplicantCount}
              listings={data.recentListings || []}
              currentPackages={data.currentPackages || []}
              listingsPath={book.jobsListings}
              hasSeekerActivity={data.hasApplications}
              canManageListings={data.hasListings}
              resumeDatabaseState={resumeAccess.data?.state}
            />
          ) : (
            <SeekerHome
              applications={data.recentApplications || []}
              applicationsPath={book.myApplications}
              hasEmployerActivity={data.hasListings || data.hasApplicantAccess}
              savedJobCount={data.savedJobCount || 0}
              hasResume={Boolean(data.hasResume)}
              resumeVisibility={data.resumeVisibility}
              resumeCompleteness={data.resumeCompleteness || 0}
              contactRequests={(contactRequests.data || []).filter((request) => request.status === 0)}
            />
          )}
        </Box>
      ) : null}
    </DashboardPage>
  );
};

export default JobsAccountHome;
